import React, { PureComponent } from "react";
import { Card, Row, Col, Spin, Select, Tag } from "antd";
import { useCubeQuery } from "@cubejs-client/react";
import { withRouter } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";
import moment from "moment";
import { AuthContext } from "../App";
// Shared color tokens (BRAND alias + statusColors / barPalette) come from theme.js.
// STATUS_COLORS, AGE_COLORS, ACTION_COLORS stay local since they are
// NeedsAction-specific (status/age/action drilldowns aren't generic enough to
// promote to the shared theme).
import { colors as BRAND, statusColors as STATUS_COLORS, barPalette as BAR_PALETTE } from "../theme";
import { fmt$, fmtNum, fmtDec, fmtTime } from "../utils/format";

const { Option } = Select;

const buildFacilitiesList = (user) => {
  const list = [];
  // Only show "All Facilities" if user has access to all orgs or multiple facilities
  if (!user || user.allOrgs || (user.facilities && user.facilities.length > 1)) {
    list.push({ name: "All Facilities", value: "" });
  }
  if (user && user.facilities) {
    user.facilities.forEach((f) => {
      list.push({ name: `${f.value} - ${f.name}`, value: f.value });
    });
  }
  if (list.length === 0) {
    list.push({ name: "All Facilities", value: "" });
  }
  return list;
};

const buildFacilityNames = (user) => {
  const map = {};
  if (user && user.facilities) {
    user.facilities.forEach((f) => { map[f.value] = f.name; });
  }
  return map;
};

const AGE_COLORS = {
  "<2": "#059669",
  "2-6": "#d97706",
  "7-14": "#ea580c",
  "15-31": "#dc2626",
  ">31": "#7f1d1d",
};

const ACTION_COLORS = {
  pending: "#2563eb",
  delayed: "#dc2626",
  missingpo: "#d97706",
  pendingbill: "#7c3aed",
};

const buildFilters = (facility, customer, product) => {
  const f = [];
  if (facility) f.push({ dimension: "AdOrg.facility", operator: "equals", values: [facility] });
  if (customer) f.push({ dimension: "CBpartner.name", operator: "equals", values: [customer] });
  if (product) f.push({ dimension: "Product.name", operator: "equals", values: [product] });
  return f;
};

// Styled tooltip wrapper
const ChartTooltip = ({ children }) => (
  <div style={{ background: "#fff", border: `1px solid ${BRAND.border}`, borderRadius: 6, padding: "10px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 13 }}>
    {children}
  </div>
);

// --- Filter dropdown ---
const DimensionSelect = ({ dimension, measure, value, onChange, placeholder, style, size }) => {
  const query = { dimensions: [dimension], order: { [dimension]: "asc" }, limit: 1000 };
  if (measure) query.measures = [measure];
  const { resultSet, isLoading } = useCubeQuery(query);
  const options = resultSet ? resultSet.tablePivot().map(r => r[dimension]).filter(Boolean) : [];
  return (
    <Select
      style={style || { width: 200 }}
      size={size}
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder}
      allowClear
      showSearch
      filterOption={(input, opt) => opt.children && opt.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
      loading={isLoading}
    >
      {options.map(o => <Option key={o} value={o}>{o}</Option>)}
    </Select>
  );
};

// --- Multi-cube filter dropdown (unions results from all cubes) ---
const MultiCubeDimensionSelect = ({ dimension, value, onChange, placeholder, style, size }) => {
  const r1 = useCubeQuery({ dimensions: [dimension], measures: ["PendingWorkOrder.pending_count"], order: { [dimension]: "asc" }, limit: 1000 });
  const r2 = useCubeQuery({ dimensions: [dimension], measures: ["DelayedSOrder.delayed_gt_48_count"], order: { [dimension]: "asc" }, limit: 1000 });
  const r3 = useCubeQuery({ dimensions: [dimension], measures: ["MissedSOrder.missing_po_count"], order: { [dimension]: "asc" }, limit: 1000 });
  const r4 = useCubeQuery({ dimensions: [dimension], measures: ["PendingWorkOrderBill.pending_count"], order: { [dimension]: "asc" }, limit: 1000 });
  const r5 = useCubeQuery({ dimensions: [dimension], measures: ["MTDRevenue.count"], order: { [dimension]: "asc" }, limit: 1000 });
  const isLoading = r1.isLoading || r2.isLoading || r3.isLoading || r4.isLoading || r5.isLoading;
  const allOptions = new Set();
  [r1, r2, r3, r4, r5].forEach(r => {
    if (r.resultSet) r.resultSet.tablePivot().forEach(row => { if (row[dimension]) allOptions.add(row[dimension]); });
  });
  const options = [...allOptions].sort();
  return (
    <Select
      style={style || { width: 200 }}
      size={size}
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder}
      allowClear
      showSearch
      filterOption={(input, opt) => opt.children && opt.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
      loading={isLoading}
    >
      {options.map(o => <Option key={o} value={o}>{o}</Option>)}
    </Select>
  );
};

// --- KPI Strip ---
const KPIStrip = ({ facility, customer, product }) => {
  const filters = buildFilters(facility, customer, product);
  const { resultSet, isLoading, error } = useCubeQuery({
    measures: ["MTDRevenue.grandtotal", "MTDRevenue.count", "MTDRevenue.avgrevenue", "MTDRevenue.wo_day", "MTDRevenue.rev_hr", "MTDRevenue.wo_time", "MTDRevenue.closed_order"],
    filters,
  });

  if (isLoading) return <div style={{ textAlign: "center", padding: 20 }}><Spin /></div>;
  if (error) return <div style={{ color: "red", padding: 8 }}>Error loading KPIs</div>;
  if (!resultSet) return null;

  const d = resultSet.totalRow();
  const monthLabel = moment().format("MMM YYYY");

  const kpis = [
    { label: "MTD Revenue", value: fmt$(d["MTDRevenue.grandtotal"]), sub: monthLabel, large: true },
    { label: "Billed Orders", value: fmtNum(d["MTDRevenue.count"]) },
    { label: "Revenue / Order", value: fmt$(d["MTDRevenue.avgrevenue"]) },
    { label: "WO / Day", value: fmtDec(d["MTDRevenue.wo_day"]) },
    { label: "Revenue / WO Hr", value: fmt$(d["MTDRevenue.rev_hr"] * 3600) },
    { label: "Time / WO", value: fmtTime(d["MTDRevenue.wo_time"]) },
    { label: "Closed WOs", value: fmtNum(d["MTDRevenue.closed_order"]) },
  ];

  return (
    <Row gutter={[12, 12]}>
      {kpis.map((k, i) => {
        const isGreen = k.large || (i % 2 === 1);
        return (
          <Col key={i} xs={k.large ? 24 : 12} sm={k.large ? 12 : 8} md={k.large ? 12 : 6} lg={k.large ? 6 : 3}>
            <div style={{
              background: isGreen ? `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryLight} 100%)` : BRAND.cardBg,
              borderRadius: 8,
              padding: "14px 18px",
              border: isGreen ? "none" : `1px solid ${BRAND.border}`,
              height: "100%",
            }}>
              <div style={{
                fontSize: 11,
                color: isGreen ? "rgba(255,255,255,0.7)" : BRAND.textMuted,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                fontWeight: 600,
              }}>
                {k.label}
              </div>
              {k.sub && <div style={{ fontSize: 11, color: isGreen ? "rgba(255,255,255,0.5)" : BRAND.textMuted, marginTop: 1 }}>{k.sub}</div>}
              <div style={{
                fontSize: k.large ? 30 : 22,
                fontWeight: 700,
                color: isGreen ? "#fff" : BRAND.text,
                marginTop: 4,
                lineHeight: 1.1,
              }}>
                {k.value}
              </div>
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

// --- Action Card with mini status bar ---
const ActionCard = ({ title, countMeasure, totalMeasure, statusDimension, facility, customer, product, color, history, reportType }) => {
  const filters = buildFilters(facility, customer, product);

  const { resultSet, isLoading } = useCubeQuery({ measures: [countMeasure, totalMeasure], filters });
  const { resultSet: statusResult } = useCubeQuery({
    measures: [countMeasure],
    dimensions: [statusDimension],
    filters,
  });

  if (isLoading) return <Card style={{ minHeight: 150, cursor: "pointer", borderRadius: 8 }} loading />;

  const data = resultSet ? resultSet.totalRow() : {};
  const count = data[countMeasure] || 0;
  const total = data[totalMeasure] || 0;

  const statusData = statusResult ? statusResult.tablePivot().map(r => ({
    status: r[statusDimension] || "N/A",
    count: Number(r[countMeasure]) || 0,
  })).sort((a, b) => b.count - a.count) : [];
  const statusTotal = statusData.reduce((s, d) => s + d.count, 0);

  return (
    <div
      style={{
        background: BRAND.cardBg,
        borderRadius: 8,
        padding: "18px 20px",
        cursor: "pointer",
        border: `1px solid ${BRAND.border}`,
        borderLeft: `4px solid ${color}`,
        minHeight: 150,
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
      onClick={() => {
        const p = new URLSearchParams();
        p.set("report", reportType);
        if (facility) p.set("facility", facility);
        if (customer) p.set("customer", customer);
        if (product) p.set("product", product);
        history.push(`/needsaction/category?${p.toString()}`);
      }}
    >
      <div style={{ fontSize: 11, color: BRAND.textMuted, letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 32, fontWeight: 700, color, lineHeight: 1 }}>{fmtNum(count)}</span>
        <span style={{ fontSize: 14, color: BRAND.textSecondary }}>{fmt$(total)}</span>
      </div>
      {statusTotal > 0 && (
        <div>
          <div style={{ display: "flex", height: 5, borderRadius: 3, overflow: "hidden", marginBottom: 8, background: "#f3f4f6" }}>
            {statusData.map((s, i) => (
              <div key={i} style={{ width: ((s.count / statusTotal) * 100) + "%", background: STATUS_COLORS[s.status] || "#9ca3af" }} title={`${s.status}: ${s.count}`} />
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px" }}>
            {statusData.map((s, i) => (
              <span key={i} style={{ fontSize: 10, color: BRAND.textSecondary, whiteSpace: "nowrap" }}>
                <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: STATUS_COLORS[s.status] || "#9ca3af", marginRight: 3, verticalAlign: "middle" }} />
                {s.status || "N/A"} {s.count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Pending Bill Card with aging bar ---
const PendingBillCard = ({ facility, customer, product, history }) => {
  const filters = buildFilters(facility, customer, product);
  const { resultSet: totalResult, isLoading: tl } = useCubeQuery({
    measures: ["PendingWorkOrderBill.pending_count", "PendingWorkOrderBill.pending_order_total"],
    filters,
  });
  const { resultSet: ageResult, isLoading: al } = useCubeQuery({
    measures: ["PendingWorkOrderBill.pending_count"],
    dimensions: ["PendingWorkOrderBill.agediff"],
    filters,
  });

  if (tl || al) return <Card style={{ minHeight: 150, borderRadius: 8 }} loading />;

  const td = totalResult ? totalResult.totalRow() : {};
  const count = td["PendingWorkOrderBill.pending_count"] || 0;
  const total = td["PendingWorkOrderBill.pending_order_total"] || 0;

  const ageRows = ageResult ? ageResult.tablePivot() : [];
  const ageBuckets = ["<2", "2-6", "7-14", "15-31", ">31"];
  const ageMap = {};
  ageRows.forEach(r => { ageMap[r["PendingWorkOrderBill.agediff"]] = Number(r["PendingWorkOrderBill.pending_count"]) || 0; });
  const ageTotal = ageBuckets.reduce((s, b) => s + (ageMap[b] || 0), 0);

  return (
    <div
      style={{
        background: BRAND.cardBg,
        borderRadius: 8,
        padding: "18px 20px",
        cursor: "pointer",
        border: `1px solid ${BRAND.border}`,
        borderLeft: `4px solid ${ACTION_COLORS.pendingbill}`,
        minHeight: 150,
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
      onClick={() => {
        const p = new URLSearchParams();
        p.set("report", "pendingbill");
        if (facility) p.set("facility", facility);
        if (customer) p.set("customer", customer);
        if (product) p.set("product", product);
        history.push(`/needsaction/category?${p.toString()}`);
      }}
    >
      <div style={{ fontSize: 11, color: BRAND.textMuted, letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>Pending to Bill</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 32, fontWeight: 700, color: ACTION_COLORS.pendingbill, lineHeight: 1 }}>{fmtNum(count)}</span>
        <span style={{ fontSize: 14, color: BRAND.textSecondary }}>{fmt$(total)}</span>
      </div>
      {ageTotal > 0 && (
        <div>
          <div style={{ display: "flex", height: 5, borderRadius: 3, overflow: "hidden", marginBottom: 8, background: "#f3f4f6" }}>
            {ageBuckets.map((b, i) => {
              const w = ((ageMap[b] || 0) / ageTotal) * 100;
              return w > 0 ? <div key={i} style={{ width: w + "%", background: AGE_COLORS[b] || "#9ca3af" }} title={`${b}d: ${ageMap[b] || 0}`} /> : null;
            })}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {ageBuckets.map(b => (
              <span key={b} style={{ fontSize: 10, color: BRAND.textSecondary, whiteSpace: "nowrap" }}>
                <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: AGE_COLORS[b], marginRight: 3, verticalAlign: "middle" }} />
                {b}d {ageMap[b] || 0}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- WO Status Donut Chart ---
const StatusDonut = ({ facility, customer, product }) => {
  const filters = buildFilters(facility, customer, product);
  const { resultSet, isLoading } = useCubeQuery({
    measures: ["PendingWorkOrder.pending_count"],
    dimensions: ["PendingWorkOrder.wo_status"],
    filters,
  });

  if (isLoading) return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;
  if (!resultSet) return null;

  const rows = resultSet.tablePivot();
  const data = rows.map(r => ({
    name: r["PendingWorkOrder.wo_status"] || "N/A",
    value: Number(r["PendingWorkOrder.pending_count"]) || 0,
  })).filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  if (data.length === 0) return <div style={{ color: BRAND.textMuted, textAlign: "center", padding: 40 }}>No data</div>;

  const total = data.reduce((s, d) => s + d.value, 0);

  const renderLabel = ({ name, value, cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill={BRAND.textSecondary} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={11}>
        {name} ({value})
      </text>
    );
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={3}
            label={renderLabel}
            labelLine={{ stroke: BRAND.border, strokeWidth: 1 }}
            isAnimationActive={false}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={STATUS_COLORS[entry.name] || "#9ca3af"} stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [fmtNum(value) + " orders", name]} />
        </PieChart>
      </ResponsiveContainer>
      {/* Center total label */}
      <div style={{ textAlign: "center", marginTop: -130, marginBottom: 100, pointerEvents: "none" }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: BRAND.text }}>{total}</div>
        <div style={{ fontSize: 10, color: BRAND.textMuted, textTransform: "uppercase" }}>Total</div>
      </div>
    </div>
  );
};

// --- Revenue by Dimension Horizontal Bar Chart ---
const RevenueByDimension = ({ facility, customer, product, dimension, onBarClick, activeValue }) => {
  const filters = buildFilters(facility, customer, product);
  const { resultSet, isLoading, error } = useCubeQuery({
    measures: ["MTDRevenue.grandtotal", "MTDRevenue.count"],
    dimensions: [dimension],
    filters,
    order: { "MTDRevenue.grandtotal": "desc" },
    limit: 8,
  });

  if (isLoading) return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;
  if (error) return <div style={{ color: "red", padding: 8 }}>Error loading data</div>;
  if (!resultSet) return null;

  const rows = resultSet.tablePivot();
  if (rows.length === 0) return <div style={{ color: BRAND.textMuted, textAlign: "center", padding: 40 }}>No data</div>;

  const data = rows.map(r => ({
    name: (r[dimension] || "N/A").length > 22 ? (r[dimension] || "N/A").substring(0, 20) + "..." : (r[dimension] || "N/A"),
    fullName: r[dimension] || "N/A",
    revenue: Number(r["MTDRevenue.grandtotal"]) || 0,
    orders: Number(r["MTDRevenue.count"]) || 0,
  }));

  const RevenueTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const d = payload[0].payload;
    return (
      <ChartTooltip>
        <div style={{ fontWeight: 600, marginBottom: 4, color: BRAND.text }}>{d.fullName}</div>
        <div>Revenue: <strong>{fmt$(d.revenue)}</strong></div>
        <div style={{ color: BRAND.textSecondary }}>Orders: {d.orders}</div>
      </ChartTooltip>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 30, bottom: 0, left: 10 }}>
        <XAxis type="number" tickFormatter={(v) => fmt$(v)} tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={{ stroke: BRAND.border }} tickLine={false} />
        <YAxis type="category" dataKey="name" width={145} tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={false} tickLine={false} />
        <Tooltip content={<RevenueTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
        <Bar dataKey="revenue" radius={[0, 4, 4, 0]} onClick={(d) => onBarClick && onBarClick(d.fullName)} cursor={onBarClick ? "pointer" : "default"}>
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={activeValue === entry.fullName ? BRAND.primary : BAR_PALETTE[i % BAR_PALETTE.length]}
              opacity={activeValue && activeValue !== entry.fullName ? 0.3 : 1}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// --- Billing Aging Bar Chart ---
const AgingChart = ({ facility, customer, product }) => {
  const filters = buildFilters(facility, customer, product);
  const { resultSet, isLoading } = useCubeQuery({
    measures: ["PendingWorkOrderBill.pending_count", "PendingWorkOrderBill.pending_order_total"],
    dimensions: ["PendingWorkOrderBill.agediff"],
    filters,
  });

  if (isLoading) return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;
  if (!resultSet) return null;

  const rows = resultSet.tablePivot();
  const buckets = ["<2", "2-6", "7-14", "15-31", ">31"];
  const ageMap = {};
  rows.forEach(r => {
    ageMap[r["PendingWorkOrderBill.agediff"]] = {
      count: Number(r["PendingWorkOrderBill.pending_count"]) || 0,
      total: Number(r["PendingWorkOrderBill.pending_order_total"]) || 0,
    };
  });

  const data = buckets.map(b => ({
    name: b + " days",
    bucket: b,
    count: (ageMap[b] || {}).count || 0,
    total: (ageMap[b] || {}).total || 0,
  }));

  const AgingTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const d = payload[0].payload;
    return (
      <ChartTooltip>
        <div style={{ fontWeight: 600, color: BRAND.text }}>{d.bucket} days old</div>
        <div>{d.count} orders</div>
        <div style={{ color: BRAND.textSecondary }}>{fmt$(d.total)} value</div>
      </ChartTooltip>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={{ stroke: BRAND.border }} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={false} tickLine={false} />
        <Tooltip content={<AgingTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
          {data.map((entry, i) => (
            <Cell key={i} fill={AGE_COLORS[entry.bucket] || "#9ca3af"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// --- Facility Comparison Chart (separate queries per cube, merged) ---
const FacilityComparison = ({ customer, product }) => {
  const user = React.useContext(AuthContext);
  const filters = [];
  if (customer) filters.push({ dimension: "CBpartner.name", operator: "equals", values: [customer] });
  if (product) filters.push({ dimension: "Product.name", operator: "equals", values: [product] });

  const { resultSet: pendingResult, isLoading: pl } = useCubeQuery({
    measures: ["PendingWorkOrder.pending_count"],
    dimensions: ["AdOrg.facility"],
    filters,
  });
  const { resultSet: delayedResult, isLoading: dl } = useCubeQuery({
    measures: ["DelayedSOrder.delayed_gt_48_count"],
    dimensions: ["AdOrg.facility"],
    filters,
  });
  const { resultSet: missingResult, isLoading: ml } = useCubeQuery({
    measures: ["MissedSOrder.missing_po_count"],
    dimensions: ["AdOrg.facility"],
    filters,
  });

  if (pl || dl || ml) return <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>;

  const facilityNames = buildFacilityNames(user);
  const facilityMap = {};

  const process = (result, key) => {
    if (!result) return;
    result.tablePivot().forEach(r => {
      const fac = r["AdOrg.facility"];
      if (!facilityMap[fac]) facilityMap[fac] = { name: facilityNames[fac] || fac || "Unknown" };
      facilityMap[fac][key] = Number(r[key]) || 0;
    });
  };

  process(pendingResult, "PendingWorkOrder.pending_count");
  process(delayedResult, "DelayedSOrder.delayed_gt_48_count");
  process(missingResult, "MissedSOrder.missing_po_count");

  const data = Object.values(facilityMap).map(f => ({
    name: f.name,
    pending: f["PendingWorkOrder.pending_count"] || 0,
    delayed: f["DelayedSOrder.delayed_gt_48_count"] || 0,
    missingPO: f["MissedSOrder.missing_po_count"] || 0,
  }));

  if (data.length === 0) return <div style={{ color: BRAND.textMuted, textAlign: "center", padding: 40 }}>No data</div>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={{ stroke: BRAND.border }} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: BRAND.textSecondary }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
        <Bar dataKey="pending" name="Pending" fill={ACTION_COLORS.pending} radius={[3, 3, 0, 0]} maxBarSize={30} />
        <Bar dataKey="delayed" name="Delayed >48h" fill={ACTION_COLORS.delayed} radius={[3, 3, 0, 0]} maxBarSize={30} />
        <Bar dataKey="missingPO" name="Missing PO" fill={ACTION_COLORS.missingpo} radius={[3, 3, 0, 0]} maxBarSize={30} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// --- Section Header ---
const SectionHeader = ({ title, subtitle }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.text }}>{title}</div>
    {subtitle && <div style={{ fontSize: 11, color: BRAND.textMuted }}>{subtitle}</div>}
  </div>
);

// --- Card wrapper for chart sections ---
const ChartCard = ({ children, title, extra, style }) => (
  <div style={{
    background: BRAND.cardBg,
    borderRadius: 8,
    border: `1px solid ${BRAND.border}`,
    padding: "20px 24px",
    height: "100%",
    ...style,
  }}>
    {title && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        {typeof title === "string" ? <SectionHeader title={title} /> : title}
        {extra}
      </div>
    )}
    {children}
  </div>
);

// === MAIN PAGE ===
class NeedsActionPage extends PureComponent {
  static contextType = AuthContext;

  constructor(props, context) {
    super(props, context);
    // Auto-select facility if user has exactly one
    const user = context;
    const defaultFacility = (user && !user.allOrgs && user.facilities && user.facilities.length === 1)
      ? user.facilities[0].value
      : "";
    this.state = { facility: defaultFacility, customer: "", product: "", revenueTab: "customer" };
  }

  render() {
    const { facility, customer, product, revenueTab } = this.state;
    const { history } = this.props;
    const user = this.context;
    const FACILITIES = buildFacilitiesList(user);
    const hasFilters = customer || product;

    const revenueTabs = [
      { key: "customer", label: "Customer", dimension: "CBpartner.name" },
      { key: "lob", label: "Line of Business", dimension: "ContainerCategory.LineOfBusiness" },
      { key: "product", label: "Product", dimension: "Product.name" },
    ];
    const activeRevTab = revenueTabs.find(t => t.key === revenueTab) || revenueTabs[0];

    return (
      <div style={{ padding: 16, background: BRAND.pageBg, minHeight: "100vh" }}>

        {/* === HEADER === */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Select size="large" style={{ minWidth: 150, flex: "1 1 150px" }} value={facility} onChange={(v) => this.setState({ facility: v })} placeholder="Facility">
              {FACILITIES.map(f => <Option key={f.value} value={f.value}>{f.name}</Option>)}
            </Select>
            <MultiCubeDimensionSelect dimension="CBpartner.name" value={customer} onChange={(v) => this.setState({ customer: v || "" })} placeholder="Customer" size="large" style={{ minWidth: 150, flex: "1 1 150px" }} />
            <MultiCubeDimensionSelect dimension="Product.name" value={product} onChange={(v) => this.setState({ product: v || "" })} placeholder="Product" size="large" style={{ minWidth: 150, flex: "1 1 150px" }} />
          </div>
        </div>

        {/* Active filter tags */}
        {hasFilters && (
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            {customer && <Tag closable onClose={() => this.setState({ customer: "" })} color="blue">Customer: {customer}</Tag>}
            {product && <Tag closable onClose={() => this.setState({ product: "" })} color="green">Product: {product}</Tag>}
            <a style={{ fontSize: 12, color: "#1890ff", cursor: "pointer" }} onClick={() => this.setState({ customer: "", product: "" })}>Clear all</a>
          </div>
        )}

        {/* === KPI STRIP === */}
        <div style={{ marginBottom: 20 }}>
          <KPIStrip facility={facility} customer={customer} product={product} />
        </div>

        {/* === ACTION CARDS ROW === */}
        <div style={{ marginBottom: 20 }}>
          <SectionHeader title="Action Items" subtitle="Click any card to drill down" />
          <Row gutter={14}>
            <Col xs={24} lg={6}>
              <ActionCard
                title="Pending Orders"
                countMeasure="PendingWorkOrder.pending_count"
                totalMeasure="PendingWorkOrder.pending_order_total"
                statusDimension="PendingWorkOrder.wo_status"
                facility={facility} customer={customer} product={product}
                color={ACTION_COLORS.pending} history={history} reportType="pending"
              />
            </Col>
            <Col xs={24} lg={6}>
              <ActionCard
                title="Delayed > 48h"
                countMeasure="DelayedSOrder.delayed_gt_48_count"
                totalMeasure="DelayedSOrder.delayed_gt_48_total"
                statusDimension="DelayedSOrder.wo_status"
                facility={facility} customer={customer} product={product}
                color={ACTION_COLORS.delayed} history={history} reportType="delayed"
              />
            </Col>
            <Col xs={24} lg={6}>
              <ActionCard
                title="Missing POs"
                countMeasure="MissedSOrder.missing_po_count"
                totalMeasure="MissedSOrder.missing_po_total"
                statusDimension="MissedSOrder.wo_status"
                facility={facility} customer={customer} product={product}
                color={ACTION_COLORS.missingpo} history={history} reportType="missingpo"
              />
            </Col>
            <Col xs={24} lg={6}>
              <PendingBillCard facility={facility} customer={customer} product={product} history={history} />
            </Col>
          </Row>
        </div>

        {/* === CHARTS ROW 1 === */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <ChartCard
              title={<SectionHeader title="MTD Revenue Breakdown" />}
              extra={
                <div style={{ display: "flex", flexWrap: "wrap", gap: 0, background: "#f9fafb", borderRadius: 6, padding: 2 }}>
                  {revenueTabs.map(t => (
                    <span
                      key={t.key}
                      onClick={() => this.setState({ revenueTab: t.key })}
                      style={{
                        fontSize: 12,
                        padding: "5px 14px",
                        cursor: "pointer",
                        borderRadius: 5,
                        background: revenueTab === t.key ? BRAND.cardBg : "transparent",
                        color: revenueTab === t.key ? BRAND.text : BRAND.textMuted,
                        fontWeight: revenueTab === t.key ? 600 : 400,
                        boxShadow: revenueTab === t.key ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                        transition: "all 0.15s",
                      }}
                    >
                      {t.label}
                    </span>
                  ))}
                </div>
              }
            >
              <RevenueByDimension
                facility={facility} customer={customer} product={product}
                dimension={activeRevTab.dimension}
                activeValue={activeRevTab.key === "customer" ? customer : activeRevTab.key === "product" ? product : null}
                onBarClick={activeRevTab.key === "customer"
                  ? (name) => this.setState({ customer: customer === name ? "" : name })
                  : activeRevTab.key === "product"
                  ? (name) => this.setState({ product: product === name ? "" : name })
                  : null
                }
              />
            </ChartCard>
          </Col>

          <Col xs={24} lg={10}>
            <ChartCard>
              <SectionHeader title="Work Order Status" subtitle="Active pending orders" />
              <StatusDonut facility={facility} customer={customer} product={product} />
            </ChartCard>
          </Col>
        </Row>

        {/* === CHARTS ROW 2 === */}
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <ChartCard>
              <SectionHeader title="Billing Aging" subtitle="Days since work order completion" />
              <AgingChart facility={facility} customer={customer} product={product} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard>
              <SectionHeader title="Facility Comparison" subtitle="Action items by facility" />
              <FacilityComparison customer={customer} product={product} />
            </ChartCard>
          </Col>
        </Row>
      </div>
    );
  }
}

export default withRouter(NeedsActionPage);
